const JELLYFIN_URL = import.meta.env.VITE_JELLYFIN_URL;
const JELLYFIN_API_KEY = import.meta.env.VITE_JELLYFIN_API_KEY;
const CORS_PROXY_URL = import.meta.env.VITE_CORS_PROXY_URL;

// Cache the movie list to avoid repeated API calls
let cachedMovies = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get the API URL - use proxy in production (HTTPS), direct for localhost
 */
const getApiUrl = (path) => {
  const useProxy = CORS_PROXY_URL && typeof window !== "undefined" && window.location.protocol === "https:";

  if (useProxy) {
    return `${CORS_PROXY_URL}/jellyfin/${path}`;
  }
  return `${JELLYFIN_URL}/${path}`;
};

/**
 * Get all movies from Jellyfin with their provider IDs
 */
const getAllJellyfinMovies = async () => {
  const now = Date.now();

  // Return cached data if still valid
  if (cachedMovies && now - cacheTimestamp < CACHE_DURATION) {
    return cachedMovies;
  }

  try {
    const response = await fetch(getApiUrl(`Items?IncludeItemTypes=Movie&Recursive=true&Fields=ProviderIds&api_key=${JELLYFIN_API_KEY}`), {
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      console.error("Jellyfin API error:", response.status);
      return [];
    }

    const data = await response.json();
    cachedMovies = data.Items || [];
    cacheTimestamp = now;

    return cachedMovies;
  } catch (error) {
    console.error("Jellyfin fetch error:", error);
    return [];
  }
};

/**
 * Check if a movie exists in the Jellyfin library by TMDB ID
 * @param {number} tmdbId - The TMDB ID of the movie
 * @returns {Promise<{found: boolean, item?: object, playUrl?: string}>}
 */
export const checkMovieInJellyfin = async (tmdbId) => {
  if (!JELLYFIN_URL || !JELLYFIN_API_KEY) {
    return { found: false, error: "Jellyfin not configured" };
  }

  try {
    const movies = await getAllJellyfinMovies();

    // Find movie by TMDB ID
    const matchingMovie = movies.find((item) => {
      if (item.ProviderIds && item.ProviderIds.Tmdb) {
        return parseInt(item.ProviderIds.Tmdb) === parseInt(tmdbId);
      }
      return false;
    });

    if (matchingMovie) {
      // Use direct Jellyfin URL for the redirect button (opens in new tab)
      const playUrl = `${JELLYFIN_URL}/web/index.html#!/details?id=${matchingMovie.Id}`;

      return {
        found: true,
        item: {
          id: matchingMovie.Id,
          name: matchingMovie.Name,
          year: matchingMovie.ProductionYear,
        },
        playUrl,
      };
    }

    return { found: false };
  } catch (error) {
    console.error("Error checking Jellyfin:", error);
    return { found: false, error: error.message };
  }
};

/**
 * Get the Jellyfin server URL for display
 */
export const getJellyfinServerUrl = () => JELLYFIN_URL;

/**
 * Get the streaming URL for a Jellyfin item
 * NOTE: Disabled until Jellyfin is served over HTTPS
 * @param {string} itemId - The Jellyfin item ID
 * @returns {null} Currently disabled
 */
export const getStreamUrl = (itemId) => {
  // Streaming disabled - requires HTTPS for Jellyfin
  console.warn("Jellyfin streaming disabled - requires HTTPS");
  return null;
};

/**
 * Get direct stream URL (no transcoding - faster if compatible)
 * NOTE: Disabled until Jellyfin is served over HTTPS
 * @param {string} itemId - The Jellyfin item ID
 * @returns {null} Currently disabled
 */
export const getDirectStreamUrl = (itemId) => {
  // Streaming disabled - requires HTTPS for Jellyfin
  console.warn("Jellyfin direct streaming disabled - requires HTTPS");
  return null;
};
